import type {
    Bandwidth,
    BasePeerConnection,
    DataChannelInit,
    TransceiverInit,
} from '@pexip/peer-connection';
import {
    createEventQueue,
    createMainPeerConnection,
    isDataChannelInit,
    isTransceiverInit,
} from '@pexip/peer-connection';
import type {AudioQualityStats} from '@pexip/peer-connection-stats';
import {createStatsCollector} from '@pexip/peer-connection-stats';
import {isEmpty, createAsyncQueue} from '@pexip/utils';

import {logger} from './logger';
import type {
    Call,
    CallOptions,
    Client,
    DataChannelEvent,
    DegradationPreference,
    IceCandidate,
    NormalizedPresentationEvent,
    PresoState,
    StatsCollectors,
    Turn,
} from './types';
import {
    getDirection,
    isAudioConfig,
    isMainConfig,
    isPresentatioRevoked,
    isPresoConfig,
    isVideoConfig,
    isPresoVideo,
    toIceCandidate,
} from './utils';
import {eventSignals} from './eventSource';
import {isNotUndefined} from './typeGuards';
import {DEFAULT_CLIENT_CALLTYPE} from './constants';

export const createCall = (options: CallOptions): Call => {
    const presoState = new Proxy(
        {
            send: 'new',
            recv: 'new',
        } as PresoState,
        {
            set: (target, p: keyof PresoState, value) => {
                const didUpdate = Reflect.set(target, p, value);
                if (didUpdate) {
                    options.callSignals.onPresentationConnectionChange.emit(
                        target,
                    );
                }
                return didUpdate;
            },
        },
    );
    const pc = createPCCall(options);

    const disconnect = () => {
        pc.cleanup();
        pc.close();

        presoState.recv = 'disconnected';
        presoState.send = 'disconnected';
    };

    const setStream = (stream: MediaStream) => {
        options.pcMainSignals.onOfferRequired.emit({
            stream,
            target: pc.peer
                .getTransceiverConfigs()
                .flatMap(config => (isMainConfig(config) ? [[config]] : [])),
            syncMedia: !pc.waitingForRemotePeer,
        });
    };

    const setBandwidth = (bandwidth: Bandwidth) => {
        pc.peer.bandwidth = bandwidth;
    };

    const setDegradationPreference = (preference: DegradationPreference) => {
        for (const config of pc.peer.getTransceiverConfigs()) {
            if (
                isMainConfig(config) &&
                isVideoConfig(config) &&
                preference.video
            ) {
                config.degradationPreference = preference.video;
            } else if (isPresoVideo(config) && preference.presentation) {
                config.degradationPreference = preference.presentation;
            }
        }
    };

    const present = async (stream?: MediaStream) => {
        if (!stream) {
            return;
        }

        logger.debug({presoState, stream}, 'present');

        try {
            presoState.send = 'connecting';
            presoState.recv = 'disconnected';

            const res = await options.takeFloor({});
            if (res?.status === 403) {
                throw new Error('Presentation rejected');
            }
            await pc.peer.setLocalStream(
                stream,
                pc.peer
                    .getTransceiverConfigs()
                    .flatMap(config =>
                        isPresoVideo(config) ? [[config, 'sendonly']] : [],
                    ),
                !pc.waitingForRemotePeer,
            );

            presoState.send = 'connected';
        } catch (error) {
            logger.warn({error, presoState, stream}, 'failed to present');
            presoState.send = 'failed';
        }
    };

    const stopPresenting = async () => {
        logger.debug({presoState}, 'stopPresenting');
        await options.releaseFloor({});
        await pc.peer.setLocalStream(
            undefined,
            pc.peer
                .getTransceiverConfigs()
                .flatMap(config =>
                    isPresoVideo(config) ? [[config, 'inactive']] : [],
                ),
            !pc.waitingForRemotePeer,
        );
        presoState.send = 'disconnected';
    };

    const receivePresentation = async (event: NormalizedPresentationEvent) => {
        if (['connecting', 'connected'].includes(presoState.recv)) {
            return;
        }
        logger.debug({presoState}, 'receivePresentation');

        presoState.recv = 'connecting' as RTCPeerConnectionState;

        const slidesConfigs = pc.peer
            .getTransceiverConfigs()
            .flatMap(config => (isPresoVideo(config) ? [config] : []));

        if (presoState.send === 'connected') {
            await pc.peer.setLocalStream(
                undefined,
                slidesConfigs.map(config => [config, 'inactive']),
                false,
            );
            await options.releaseFloor({});
            presoState.send = 'disconnected';
        }

        if (presoState.recv === 'disconnected') {
            return;
        }

        if (isPresentatioRevoked(event)) {
            presoState.recv = 'disconnected';
            return;
        }

        const [slidesConfig] = slidesConfigs;
        if (slidesConfig) {
            slidesConfig.direction = 'recvonly';
            slidesConfig.syncDirection();
        }

        presoState.recv = 'connected';
    };

    const stopReceivingPresentation = () => {
        logger.debug({presoState}, 'stopReceivingPresentation');
        if (
            presoState.recv === 'connected' ||
            presoState.recv === 'connecting'
        ) {
            presoState.recv = 'disconnected';
        }
        const [slidesConfig] = pc.peer
            .getTransceiverConfigs()
            .flatMap(config => (isPresoVideo(config) ? [config] : []));
        if (
            slidesConfig &&
            presoState.send !== 'connected' &&
            presoState.send !== 'connecting'
        ) {
            slidesConfig.direction = 'inactive';
            slidesConfig.syncDirection();
        }
    };

    const sendDataChannelEvent = (event: DataChannelEvent) => {
        if (!pc.dataChannel || pc.dataChannel.readyState !== 'open') {
            logger.warn(
                {eventType: event.type},
                'DataChannel not open. Cannot send event.',
            );
            return;
        }
        pc.dataChannel.send(JSON.stringify(event));
    };

    return {
        get presoState() {
            return presoState;
        },
        get mediaStream() {
            const config = pc.peer.getTransceiverConfigs().find(config => {
                return isMainConfig(config) && config.streams[0];
            });
            return config?.streams[0];
        },
        get bandwidth() {
            return pc.peer.bandwidth;
        },
        disconnect,
        present,
        receivePresentation,
        setBandwidth,
        setDegradationPreference,
        setStream,
        stopPresenting,
        stopReceivingPresentation,
        sendDataChannelEvent,
    };
};

const createPCCall = ({
    ack,
    sendOffer,
    update,
    newCandidate,
    peerOptions,
    signals,
    pcMainSignals,
    mainStatsSignals,
    mediaStream,
    callSignals,
    dataChannelId,
    isDirectMedia,
    callType = DEFAULT_CLIENT_CALLTYPE,
    encodingPriorities = {video: 'low', audio: 'low', presentation: 'low'},
    networkPriorities,
    degradationPreference,
    jitterBufferTarget,
    allowChangeOfMediaDirection = {
        video: true,
        audio: true,
        presentation: true,
    },
    timeToWaitForOfferInMS = 2000,
}: CallOptions) => {
    let called = false;
    /**
     * With direct media, we should not sync the transceivers until the remote peer is present.
     * This means having set the remote description to an offer and the local to an answer.
     */
    let waitingForRemotePeer = false;
    let turn443 = false;
    let apiOneTimeCallbacks: Partial<Record<keyof Client, Array<() => void>>> =
        {};
    let videoPacketsReceived = 0;
    const incomingICECandidateQueue = createEventQueue(
        pcMainSignals.onReceiveIceCandidate.emit,
    );
    let updateController: undefined | AbortController;
    let offerIgnoredTimeout = 0;

    const clearOfferIgnoredTimeout = () => {
        clearTimeout(offerIgnoredTimeout);
        offerIgnoredTimeout = 0;
    };

    const queue = createAsyncQueue({
        throttleInMS: 0,
        delayInMS: 0,
        size: 100,
        timeoutInMS: 15_000,
        handleTimeout: async () => {
            logger.info(
                {updateController, isDirectMedia, called},
                'Handle Signaling request timeout',
            );
            updateController?.abort();
            queue.empty();
            releaseIncomingCandidateBuffer();
            releaseOutGoingCandidateBuffer();
            if (isDirectMedia) {
                logger.info(
                    {updateController, isDirectMedia, called},
                    'Restarting Peer Connection for direct media due to signaling timeout',
                );
                peer.close();
                peer = createPC(peerOptions.rtcConfig, true);
                waitingForRemotePeer = true;
            }
        },
    });

    const statsCollectors: StatsCollectors = {inbound: {}, outbound: {}};
    const createPC = (rtcConfig = peerOptions.rtcConfig, polite?: boolean) => {
        const audioDirection = getDirection('audio', callType);
        const videoDirection = getDirection('video', callType);
        const presentationDirection = getDirection('presentation', callType);
        const [audioTrack] = mediaStream?.getAudioTracks() ?? [];
        const [videoTrack] = mediaStream?.getVideoTracks() ?? [];
        const streams = mediaStream && [mediaStream];
        const peer = createMainPeerConnection(pcMainSignals, {
            ...peerOptions,
            polite,
            rtcConfig,
            mediaInits: [
                audioDirection
                    ? ({
                          content: 'main',
                          direction: audioDirection,
                          kindOrTrack: audioTrack ?? 'audio',
                          streams,
                          allowChangeOfMediaDirection:
                              allowChangeOfMediaDirection.audio,
                          relativeDirection: isDirectMedia,
                          sendEncodings: [
                              {
                                  ...(encodingPriorities.audio && {
                                      priority: encodingPriorities.audio,
                                  }),
                                  ...(networkPriorities?.audio && {
                                      networkPriority: networkPriorities?.audio,
                                  }),
                              },
                          ],
                          ...(jitterBufferTarget?.audio !== undefined && {
                              jitterBufferTarget: jitterBufferTarget?.audio,
                          }),
                      } satisfies TransceiverInit)
                    : false,
                videoDirection
                    ? {
                          content: 'main',
                          direction: videoDirection,
                          kindOrTrack: videoTrack ?? 'video',
                          streams,
                          allowChangeOfMediaDirection:
                              allowChangeOfMediaDirection.video,
                          relativeDirection: isDirectMedia,
                          sendEncodings: [
                              {
                                  ...(encodingPriorities.video && {
                                      priority: encodingPriorities.video,
                                  }),
                                  ...(networkPriorities?.video && {
                                      networkPriority: networkPriorities?.video,
                                  }),
                              },
                          ],
                          ...(degradationPreference?.video && {
                              degradationPreference:
                                  degradationPreference?.video,
                          }),
                          ...(jitterBufferTarget?.video !== undefined && {
                              jitterBufferTarget: jitterBufferTarget?.video,
                          }),
                      }
                    : false,
                presentationDirection
                    ? {
                          content: 'slides',
                          direction: 'inactive',
                          kindOrTrack: 'video',
                          allowChangeOfMediaDirection:
                              allowChangeOfMediaDirection.presentation,
                          sendEncodings: [
                              {
                                  ...(encodingPriorities.presentation && {
                                      priority: encodingPriorities.presentation,
                                  }),
                                  ...(networkPriorities?.presentation && {
                                      networkPriority:
                                          networkPriorities?.presentation,
                                  }),
                              },
                          ],
                          ...(degradationPreference?.presentation && {
                              degradationPreference:
                                  degradationPreference?.presentation,
                          }),
                          ...(jitterBufferTarget?.presentation !==
                              undefined && {
                              jitterBufferTarget:
                                  jitterBufferTarget?.presentation,
                          }),
                      }
                    : false,
                dataChannelId
                    ? ({
                          label: 'pexChannel',
                          negotiated: true,
                          id: dataChannelId,
                          eventListeners: [
                              {
                                  event: 'message',
                                  listener: event => {
                                      try {
                                          const msg = JSON.parse(
                                              event.data,
                                          ) as DataChannelEvent;

                                          switch (msg.type) {
                                              case 'message':
                                                  eventSignals.onMessage.emit({
                                                      ...msg.body,
                                                      direct: false,
                                                  });
                                                  break;

                                              case 'fecc':
                                                  eventSignals.onFecc.emit(
                                                      msg.body,
                                                  );
                                                  break;
                                          }
                                      } catch (error) {
                                          logger.error(
                                              {error, event},
                                              'Failed to parse DataChannel event data from server.',
                                          );
                                      }
                                  },
                              },
                          ],
                      } satisfies DataChannelInit)
                    : false,
            ].flatMap(init =>
                isTransceiverInit(init) || isDataChannelInit(init)
                    ? [init]
                    : [],
            ),
        });
        return peer;
    };

    let peer = createPC();

    const addIceServers = (iceServers: RTCIceServer[]) => {
        const rtcConfig = peer.getConfiguration();
        return isEmpty(iceServers)
            ? rtcConfig
            : {
                  ...rtcConfig,
                  iceServers: [...(rtcConfig.iceServers ?? []), ...iceServers],
              };
    };

    const executeOneTimeApiCallback = (func: keyof Client) => {
        const callbacks = apiOneTimeCallbacks[func];
        if (callbacks) {
            for (const cb of callbacks) {
                cb();
            }
            apiOneTimeCallbacks[func] = undefined;
        }
    };

    const sendCandidate = (candidate: IceCandidate) => {
        queue.enqueue(async () => {
            await newCandidate({
                candidate,
            });
            executeOneTimeApiCallback('newCandidate');
        });
    };

    // Outgoing ICE Candidate buffer is needed since the initial offer could be
    // ignored and the Peer Connection is re-created.
    const outgoingICECandidateQueue = createEventQueue(sendCandidate);

    const addOnceApiCallback = (func: keyof Client, cb: () => void) => {
        const cbArray = apiOneTimeCallbacks[func];
        if (cbArray) {
            cbArray.push(cb);
        } else {
            apiOneTimeCallbacks[func] = [cb];
        }
    };

    const createStatsCollectors = () => {
        for (const config of peer.getTransceiverConfigs()) {
            if (isMainConfig(config) && config.transceiver) {
                statsCollectors.inbound[config.kind] = createStatsCollector({
                    input: config.transceiver.receiver,
                    signals: isAudioConfig(config)
                        ? mainStatsSignals.audioIn
                        : mainStatsSignals.videoIn,
                });
                statsCollectors.outbound[config.kind] = createStatsCollector({
                    input: config.transceiver.sender,
                    signals: isAudioConfig(config)
                        ? mainStatsSignals.audioOut
                        : mainStatsSignals.videoOut,
                });
            }
            if (isPresoVideo(config) && config.transceiver) {
                statsCollectors.inbound.preso = createStatsCollector({
                    input: config.transceiver.receiver,
                    signals: mainStatsSignals.presoVideoIn,
                });
                statsCollectors.outbound.preso = createStatsCollector({
                    input: config.transceiver.sender,
                    signals: mainStatsSignals.presoVideoOut,
                });
            }
        }
    };

    const cleanupStats = () => {
        statsCollectors.inbound.audio?.cleanup();
        statsCollectors.outbound.audio?.cleanup();
        statsCollectors.inbound.video?.cleanup();
        statsCollectors.outbound.video?.cleanup();
        statsCollectors.inbound.preso?.cleanup();
        statsCollectors.outbound.preso?.cleanup();
    };

    const restartIce = (targetPeer: BasePeerConnection) => {
        if (!['new', 'closed'].includes(targetPeer.connectionState)) {
            releaseIncomingCandidateBuffer();
            releaseOutGoingCandidateBuffer();
            targetPeer.restartIce();
        }
    };

    const handleAck = (turn?: Turn) => {
        turn443 = false;
        if (!turn) {
            return;
        }

        const rtcConfig = addIceServers(turn);
        if (peer.setConfiguration) {
            peer.setConfiguration(rtcConfig);
            peer.restartIce();
        } else {
            peer.close();
            peer = createPC(rtcConfig);
            pcMainSignals.onOfferRequired.emit(
                mediaStream && {
                    stream: mediaStream,
                    target: peer
                        .getTransceiverConfigs()
                        .flatMap(config =>
                            isMainConfig(config) ? [[config]] : [],
                        ),
                },
            );
        }
    };

    const releaseIncomingCandidateBuffer = () => {
        incomingICECandidateQueue.buffering = false;
        if (incomingICECandidateQueue.length) {
            const candidatesFlushed = incomingICECandidateQueue.flush();
            logger.debug(
                {incomingCandidates: candidatesFlushed},
                'release buffered incoming candidates',
            );
        }
    };
    const releaseOutGoingCandidateBuffer = () => {
        outgoingICECandidateQueue.buffering = false;
        if (outgoingICECandidateQueue.length) {
            const candidatesFlushed = outgoingICECandidateQueue.flush();
            logger.debug(
                {outgoingCandidates: candidatesFlushed},
                'release buffered outgoing candidates',
            );
        }
    };

    let detachApiSignals = [
        signals.onAnswer.add(({sdp, turn, offer_ignored}) => {
            if (isDirectMedia && (offer_ignored || !sdp)) {
                // Discard the call and wait for `new_offer` for direct media
                // Assign polite peer role to the new Peer Connection instance
                logger.info(
                    {sdp, turn, offer_ignored, called},
                    'Offer being ignored',
                );
                // ICE candidates for the previous Peer Connection are obsolete
                releaseOutGoingCandidateBuffer();
                queue.empty();
                const restartPC = () => {
                    logger.info(
                        {sdp, turn, offer_ignored, called},
                        'Restarting Peer Connection',
                    );
                    peer.close();
                    peer = createPC(peerOptions.rtcConfig, true);
                    waitingForRemotePeer = true;
                    offerIgnoredTimeout = 0;
                };
                if (called) {
                    offerIgnoredTimeout = setTimeout(
                        restartPC,
                        timeToWaitForOfferInMS,
                    );
                } else {
                    restartPC();
                }
                return;
            }
            if (offer_ignored || !sdp) {
                return;
            }
            releaseOutGoingCandidateBuffer();
            const answer = new RTCSessionDescription({
                sdp,
                type: 'answer',
            });
            turn443 = Boolean(turn);

            queue.enqueue(async () => {
                await ack({});
                handleAck(turn);
            });

            pcMainSignals.onReceiveAnswer.emit(answer);
            releaseIncomingCandidateBuffer();
        }),
        signals.onNewOffer.add(sdp => {
            if (!sdp) {
                return;
            }
            const offer = new RTCSessionDescription({
                sdp,
                type: 'offer',
            });
            // No need to buffer out going ICE candidates
            releaseOutGoingCandidateBuffer();
            pcMainSignals.onReceiveOffer.emit(offer);
            releaseIncomingCandidateBuffer();
        }),
        signals.onUpdateSdp.add(sdp => {
            if (!sdp) {
                return;
            }
            if (offerIgnoredTimeout) {
                clearOfferIgnoredTimeout();
            }
            const offer = new RTCSessionDescription({
                sdp,
                type: 'offer',
            });
            // No need to buffer out going ICE candidates
            releaseOutGoingCandidateBuffer();
            pcMainSignals.onReceiveOffer.emit(offer);
            releaseIncomingCandidateBuffer();
        }),
        signals.onIceCandidate.add(candidate => {
            // Strip unnecessary properties to please Safari when end-of-candidates.
            if (candidate.candidate === '') {
                // Manually indicated end-of-candidates
                candidate = {candidate: ''} as RTCIceCandidate;
            }
            if (incomingICECandidateQueue.buffering) {
                logger.debug({candidate}, 'buffering incoming candidates');
            }
            incomingICECandidateQueue.enqueue(candidate);
        }),
    ];

    let detachPCSignals = [
        pcMainSignals.onOffer.add(({sdp}) => {
            if (!sdp) {
                return;
            }
            incomingICECandidateQueue.buffering = true;
            queue.enqueue(async () => {
                if (called) {
                    updateController = new AbortController();
                    await update({sdp, abortSignal: updateController.signal});
                    updateController = undefined;
                } else {
                    await sendOffer({sdp});
                    called = true;
                }
            });
        }),

        pcMainSignals.onOfferIgnored.add(() => {
            void ack({offerIgnored: true});
        }),

        pcMainSignals.onAnswer.add(({sdp}) => {
            waitingForRemotePeer = false;
            updateController?.abort(); // Abort current update call
            queue.enqueue(async () => {
                await ack({sdp});
                handleAck();
            });
        }),

        pcMainSignals.onRemoteStreams.add(transceiverConfig => {
            logger.debug(
                {transceiverConfig, isDirectMedia},
                'Received remote streams',
            );
            const signal = isPresoConfig(transceiverConfig)
                ? callSignals.onRemotePresentationStream
                : callSignals.onRemoteStream;
            if (isDirectMedia && transceiverConfig.remoteStreams) {
                for (const stream of transceiverConfig.remoteStreams) {
                    logger.debug(
                        {
                            stream,
                            tracks: stream.getTracks(),
                        },
                        'Emits remote stream',
                    );
                    signal.emit(stream);
                }
                return;
            }
            const tracks = (
                isPresoConfig(transceiverConfig)
                    ? [transceiverConfig.transceiver?.receiver.track]
                    : peer
                          .getTransceiverConfigs()
                          .filter(isMainConfig)
                          .map(config => config.transceiver?.receiver.track)
            ).filter(isNotUndefined);
            logger.debug(
                {
                    transceiverConfig,
                    transceiverConfigs: peer.getTransceiverConfigs(),
                    tracks,
                },
                'Emits remote streams based on transceivers',
            );
            signal.emit(new MediaStream(tracks));
        }),

        pcMainSignals.onIceCandidate.add(event => {
            if (turn443) {
                // no need to send dead candidates as we will be doing a restart on TURN443 in answer
                return;
            }
            const candidate = toIceCandidate(event);
            outgoingICECandidateQueue.enqueue(candidate);
        }),

        pcMainSignals.onConnectionStateChange.add(connectionState => {
            switch (connectionState) {
                case 'connected': {
                    callSignals.onCallConnected.emit();
                    break;
                }
                case 'closed': {
                    signals.onError.emit({
                        error: 'WebRTC connection closed',
                        errorCode: '#pex117',
                    });
                    break;
                }
                case 'failed': {
                    signals.onError.emit({
                        error: 'WebRTC connection failed',
                        errorCode: '#pex128',
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        }),

        pcMainSignals.onSignalingStateChange.add(state => {
            if (state === 'closed') {
                signals.onError.emit({
                    error: 'WebRTC connection closed',
                    errorCode: '#pex117',
                });
            }
        }),

        pcMainSignals.onError.add(error => {
            logger.error(error.context);
            // FIXME: we need to handle the error properly
        }),

        pcMainSignals.onTransceiverChange.add(() => {
            cleanupStats();
            createStatsCollectors();
        }),

        pcMainSignals.onSecureCheckCode.add(callSignals.onSecureCheckCode.emit),

        mainStatsSignals.combinedRtcStatsSignal.add(
            ([
                audioIn,
                audioOut,
                videoIn,
                videoOut,
                presoVideoIn,
                presoVideoOut,
            ]) => {
                const stats = {
                    inbound: {
                        audio: audioIn,
                        video: videoIn,
                        preso: presoVideoIn,
                    },
                    outbound: {
                        audio: audioOut,
                        video: videoOut,
                        preso: presoVideoOut,
                    },
                };
                callSignals.onRtcStats.emit(stats);
            },
        ),

        mainStatsSignals.combinedCallQualityStatsSignal.add(
            ([audioIn, audioOut]) => {
                callSignals.onCallQualityStats.emit({
                    inbound: {audio: audioIn as AudioQualityStats}, // FIXME: stats can be either AudioQualityStats | VideoQualityStats
                    outbound: {audio: audioOut as AudioQualityStats},
                });
            },
        ),

        mainStatsSignals.combinedCallQualitySignal.add(
            callSignals.onCallQuality.emit,
        ),

        mainStatsSignals.videoIn.onRtcStats.add(stats => {
            if (stats) {
                const update = stats.packetsTransmitted;
                if (update < videoPacketsReceived) {
                    // likely a stale update from just recovered connection
                    return;
                }
                if (
                    videoPacketsReceived !== 0 &&
                    videoPacketsReceived === update
                ) {
                    logger.info(
                        'Triggering ICE restart due to packet stagnation',
                    );
                    const resumeStatsUpdates = [
                        statsCollectors.inbound.video?.resetStats(),
                        statsCollectors.outbound.video?.resetStats(),
                        statsCollectors.inbound.audio?.resetStats(),
                        statsCollectors.outbound.audio?.resetStats(),
                    ];

                    addOnceApiCallback('newCandidate', () => {
                        /**
                         * Stats could start flowing before new packets.
                         * Increment the received packets in order to avoid false negatives
                         */
                        videoPacketsReceived++;
                        for (const resume of resumeStatsUpdates) {
                            resume?.();
                        }
                        callSignals.onReconnected.emit();
                    });
                    callSignals.onReconnecting.emit();
                    restartIce(peer);
                }
                videoPacketsReceived = update;
            }
        }),
    ];

    const cleanup = () => {
        detachApiSignals = detachApiSignals.flatMap(detach => {
            detach();
            return [];
        });
        detachPCSignals = detachPCSignals.flatMap(detach => {
            detach();
            return [];
        });
        cleanupStats();
        apiOneTimeCallbacks = {};
    };

    const close = () => {
        called = false;
        waitingForRemotePeer = false;
        releaseIncomingCandidateBuffer();
        releaseOutGoingCandidateBuffer();
        queue.empty();
        clearOfferIgnoredTimeout();
        if (peer.connectionState === 'closed') {
            return;
        }
        peer.close();
    };

    // Start negotiation after successfully subscribing to all signals
    pcMainSignals.onOfferRequired.emit({
        syncMedia: !waitingForRemotePeer,
    });

    return {
        get peer() {
            return peer;
        },
        get dataChannel() {
            const [config] = peer.getDataChannelConfigs();
            return config?.dataChannel;
        },
        get waitingForRemotePeer() {
            return waitingForRemotePeer;
        },
        cleanup,
        close,
    };
};
